import { Component, OnInit } from '@angular/core';
import { AdminService } from '../../../services/admin/admin.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-broker-manager',
  templateUrl: './broker-manager.component.html',
  styleUrls: ['./broker-manager.component.scss']
})
export class BrokerManagerComponent implements OnInit {
  brokers: any[] = [];
  loading = true;
  error = '';
  successMsg = '';

  // Modal states
  settleModalBroker: any = null;
  settleAmount: number = 0;
  settling = false;

  commissionModalBroker: any = null;
  newCommissionRate: number = 15;
  updatingCommission = false;

  // Review Application Modal state
  reviewModalBroker: any = null;
  reviewCommissionRate: number = 15;
  savingReview = false;

  constructor(private adminService: AdminService) {}

  ngOnInit(): void {
    this.loadBrokers();
  }

  getImageUrl(urlStr: string): string {
    if (!urlStr) return '';
    if (urlStr.startsWith('http') || urlStr.startsWith('data:')) {
      return urlStr;
    }
    return `${environment.apiUrl}/auth/image/${urlStr}`;
  }

  loadBrokers(): void {
    this.loading = true;
    this.error = '';
    this.adminService.getBrokers().subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.brokers = res.data || [];
        } else {
          this.error = res.message || 'Failed to load brokers';
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error connecting to server';
        this.loading = false;
      }
    });
  }

  openSettleModal(broker: any): void {
    this.settleModalBroker = broker;
    this.settleAmount = broker.wallet_balance || 0;
  }

  closeSettleModal(): void {
    this.settleModalBroker = null;
  }

  confirmSettle(): void {
    if (!this.settleModalBroker || this.settleAmount <= 0) return;
    this.settling = true;
    this.adminService.settleBrokerPayout(this.settleModalBroker._id, this.settleAmount).subscribe({
      next: (res) => {
        this.settling = false;
        if (res.status === 'success') {
          this.showSuccess(`Settled ₹${this.settleAmount} for ${this.settleModalBroker.name}`);
          this.closeSettleModal();
          this.loadBrokers();
        } else {
          alert(res.message || 'Error settling payout');
        }
      },
      error: (err) => {
        this.settling = false;
        alert(err.error?.message || 'Error settling payout');
      }
    });
  }

  openCommissionModal(broker: any): void {
    this.commissionModalBroker = broker;
    this.newCommissionRate = broker.commission_rate !== undefined ? broker.commission_rate : 15;
  }

  closeCommissionModal(): void {
    this.commissionModalBroker = null;
  }

  confirmCommission(): void {
    if (!this.commissionModalBroker) return;
    this.updatingCommission = true;
    this.adminService.updateBrokerCommission(this.commissionModalBroker._id, this.newCommissionRate).subscribe({
      next: (res) => {
        this.updatingCommission = false;
        if (res.status === 'success') {
          this.showSuccess(`Commission rate updated to ${this.newCommissionRate}%`);
          this.closeCommissionModal();
          this.loadBrokers();
        } else {
          alert(res.message || 'Error updating commission');
        }
      },
      error: (err) => {
        this.updatingCommission = false;
        alert(err.error?.message || 'Error updating commission');
      }
    });
  }

  openReviewModal(broker: any): void {
    this.reviewModalBroker = { ...broker };
    this.reviewCommissionRate = broker.commission_rate !== undefined ? broker.commission_rate : 15;
  }

  closeReviewModal(): void {
    this.reviewModalBroker = null;
  }

  saveReviewChanges(): void {
    if (!this.reviewModalBroker) return;
    this.savingReview = true;
    this.adminService.updateBrokerCommission(this.reviewModalBroker._id, this.reviewCommissionRate).subscribe({
      next: (res) => {
        this.savingReview = false;
        if (res.status === 'success') {
          this.showSuccess(`Saved commission rate for ${this.reviewModalBroker.name}`);
          this.closeReviewModal();
          this.loadBrokers();
        } else {
          alert(res.message || 'Error saving changes');
        }
      },
      error: (err) => {
        this.savingReview = false;
        alert(err.error?.message || 'Error saving changes');
      }
    });
  }

  setStatusFromReview(status: string): void {
    if (!this.reviewModalBroker) return;
    const actionText = status === 'active' ? 'Approve & Activate' : status === 'suspended' ? 'Suspend' : 'Reject';
    if (!confirm(`Are you sure you want to ${actionText} broker ${this.reviewModalBroker.name}?`)) return;
    
    this.savingReview = true;
    this.adminService.updateBrokerStatus(this.reviewModalBroker._id, status).subscribe({
      next: (res) => {
        this.savingReview = false;
        if (res.status === 'success') {
          this.showSuccess(`Broker ${this.reviewModalBroker.name} is now ${status.toUpperCase()}`);
          this.closeReviewModal();
          this.loadBrokers();
        }
      },
      error: (err) => {
        this.savingReview = false;
        alert(err.error?.message || 'Error updating status');
      }
    });
  }

  toggleStatus(broker: any): void {
    const newStatus = broker.status === 'active' ? 'suspended' : 'active';
    if (!confirm(`Are you sure you want to set ${broker.name} to ${newStatus}?`)) return;
    
    this.adminService.updateBrokerStatus(broker._id, newStatus).subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.showSuccess(`Broker status updated to ${newStatus}`);
          this.loadBrokers();
        }
      },
      error: (err) => {
        alert(err.error?.message || 'Error updating status');
      }
    });
  }

  setStatus(broker: any, status: string): void {
    const actionText = status === 'active' ? 'Approve & Activate' : status === 'suspended' ? 'Reject & Suspend' : status;
    if (!confirm(`Are you sure you want to ${actionText} broker application for ${broker.name}?`)) return;
    
    this.adminService.updateBrokerStatus(broker._id, status).subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.showSuccess(`Broker ${broker.name} is now ${status.toUpperCase()}`);
          this.loadBrokers();
        }
      },
      error: (err) => {
        alert(err.error?.message || 'Error updating status');
      }
    });
  }

  showSuccess(msg: string): void {
    this.successMsg = msg;
    setTimeout(() => { this.successMsg = ''; }, 4000);
  }
}
